import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import Navbar from '../../layout/Navbar';
import Footer from '../../layout/Footer';

const FullPackage = () => {
    const navigate = useNavigate();
   
    const config = {
        apiUrl: 'https://pastq.eddcodesgh.com/qApi/client/package/initiate-expresspay.php',
        apiKey: 'f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506',
    };
    
    // Available years (2020-2024 as example)
    const availableYears = [2025, 2024, 2023, 2022, 2021, 2020];
    
    const examLevels = [
        {
            id: "BECE",
            name: "BECE",
            fullName: "Basic Education Certificate Examination",
            description: "Junior High School (JHS) Level",
            color: "primary",
            icon: "bi-mortarboard",
            subjects: "English, Mathematics, Integrated Science, Social Studies",
            features: ["Complete Question Papers", "Marking Schemes", "Answer Booklets", "Examiner's Reports"]
        },
        {
            id: "WASSCE",
            name: "WASSCE",
            fullName: "West African Senior School Certificate Examination",
            description: "Senior High School (SHS) Level",
            color: "success",
            icon: "bi-book",
            subjects: "Core + 4 Electives of your choice",
            features: ["All Papers Included", "Practical Questions", "Objective Tests", "Essay Questions"]
        },
        {
            id: "CTVET",
            name: "CTVET",
            fullName: "Commission For Technical and Vocation Education Training",
            description: "Alternative CTVET Examination",
            color: "warning",
            icon: "bi-award",
            subjects: "Core Subjects + Selected Electives",
            features: ["Full Question Sets", "Answer Guides", "Study Tips"]
        }
    ];

    // State management
    const [selectedYear, setSelectedYear] = useState(null);
    const [selectedLevel, setSelectedLevel] = useState(null);
    const [userInfo, setUserInfo] = useState({
        firstname: "",
        lastname: "",
        email: "",
        phone: ""
    });
    const [validationErrors, setValidationErrors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);

    const handleYearSelect = (year) => {
        setSelectedYear(year);
        setSelectedLevel(null);
        setShowForm(false);
        setValidationErrors([]);
    };

    const handleLevelSelect = (level) => {
        if (!selectedYear) {
            toast.error("Please select an exam year first");
            return;
        }
        setSelectedLevel(level);
        setShowForm(true);
        
        // Scroll to form
        setTimeout(() => {
            document.getElementById("purchase-form")?.scrollIntoView({ behavior: "smooth" });
        }, 100);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setUserInfo(prev => ({
            ...prev,
            [name]: value
        }));
        
        if (validationErrors.length > 0) {
            setValidationErrors(prev => prev.filter(error => 
                !error.toLowerCase().includes(name)
            ));
        }
    };

    // Validate email format
    const validateEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    // Validate Ghana phone number format
    const validatePhone = (phone) => {
        if (!phone.trim()) return false;
        const re = /^(\+233|0)[235]\d{8}$/;
        return re.test(phone.replace(/\s/g, ''));
    };

    // Validate form before submission
    const validateForm = () => {
        const errors = [];
        
        // Check first name
        if (!userInfo.firstname.trim()) {
            errors.push("First name is required");
        } else if (userInfo.firstname.trim().length < 2) {
            errors.push("First name must be at least 2 characters");
        }
        
        // Check last name
        if (!userInfo.lastname.trim()) {
            errors.push("Last name is required");
        } else if (userInfo.lastname.trim().length < 2) {
            errors.push("Last name must be at least 2 characters");
        }
        
        // Check email
        if (!userInfo.email.trim()) {
            errors.push("Email is required");
        } else if (!validateEmail(userInfo.email)) {
            errors.push("Invalid email format");
        }
        
        // Check phone
        if (!userInfo.phone.trim()) {
            errors.push("Phone number is required");
        } else if (!validatePhone(userInfo.phone)) {
            errors.push("Invalid Ghanaian phone number. Format: +233XXXXXXXXX or 0XXXXXXXXX");
        }
        
        setValidationErrors(errors);
        return errors.length === 0;
    };

    const displayValidationErrors = () => {
        if (validationErrors.length === 0) return null;
        
        return (
            <div className="alert alert-danger">
                <h6 className="mb-2">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    Please fix the following errors:
                </h6>
                <ul className="mb-0">
                    {validationErrors.map((error, index) => (
                        <li key={index}>{error}</li>
                    ))}
                </ul>
            </div>
        );
    };

    const formatPhoneNumber = (phone) => {
        // Remove all non-digit characters except + sign
        let cleaned = phone.replace(/[^\d+]/g, '');
        
        if (cleaned.startsWith('0')) {
            cleaned = '+233' + cleaned.substring(1);
        }
 
        if (cleaned.startsWith('233') && !cleaned.startsWith('+233')) {
            cleaned = '+' + cleaned;
        }
        
        return cleaned;
    };

    // Handle payment initiation
    const handlePayment = async () => {
        setValidationErrors([]);
        
        // Validate form
        if (!validateForm()) {
            toast.error("Please fix the validation errors");
            return;
        }
        
        try {
            setLoading(true);
            
            // Generate unique order ID
            const orderId = `PQ_${selectedYear}_${selectedLevel.id}_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
            
            // Format phone number
            const formattedPhone = formatPhoneNumber(userInfo.phone);
            const paymentData = {
                order_id: orderId,
                amount: 27.00, // Fixed price 27
                firstname: userInfo.firstname.trim(),
                lastname: userInfo.lastname.trim(),
                email: userInfo.email.trim(),
                phonenumber: formattedPhone,
                exam_year: selectedYear,
                exam_level: selectedLevel.name,
                exam_level_name: selectedLevel.fullName,
                description: `${selectedLevel.name} ${selectedYear} Complete Past Questions Package`,
                metadata: {
                    year: selectedYear,
                    level: selectedLevel.id,
                    level_name: selectedLevel.fullName,
                    subjects: selectedLevel.subjects
                }
            };
            
            console.log("Sending payment data:", paymentData);
            
            // Store order in localStorage for reference
            localStorage.setItem('package_order', JSON.stringify({
                ...paymentData,
                userInfo,
                selectedYear,
                selectedLevel,
                timestamp: new Date().toISOString()
            }));
            
            // Show loading message
            toast.info("Initiating payment...");
            
            // Call payment API
            const response = await axios.post(config.apiUrl, paymentData, {
                headers: {
                    'Authorization': `Bearer ${config.apiKey}`,
                    'Content-Type': 'application/json'
                },
                timeout: 30000
            });
            
            // console.log('Payment API Response:', response.data);
            
            if (response.data.status === 'success') {
                // Save payment reference
                localStorage.setItem('payment_reference', response.data.data.reference || orderId);
                
                toast.success("Payment initiated! Redirecting...");
                
                // Redirect to payment gateway
                setTimeout(() => {
                    const paymentUrl = response.data.data.checkoutUrl || 
                                      response.data.data.paymentUrl || 
                                      response.data.data.redirectUrl;
                    
                    if (paymentUrl) {
                        window.location.href = paymentUrl;
                    } else {
                        toast.error("No payment URL received. Please contact support.");
                        setLoading(false);
                    }
                }, 1500);
                
            } else {
                // Handle API validation errors
                if (response.data.errors && Array.isArray(response.data.errors)) {
                    setValidationErrors(response.data.errors);
                    toast.error("Validation failed. Please check the errors below.");
                } else {
                    const errorMsg = response.data.message || 'Payment initiation failed';
                    toast.error(errorMsg);
                }
                setLoading(false);
            }
            
        } catch (error) {
            console.error("Payment error:", error);
            
            // Handle API validation errors from response
            if (error.response?.data?.errors) {
                setValidationErrors(error.response.data.errors);
                toast.error("Validation failed. Please check the errors below.");
            } else {
                const errorMessage = error.response?.data?.message || 
                                    error.response?.data?.error || 
                                    error.message || 
                                    'Network error. Please try again.';
                
                toast.error(errorMessage);
            }
            setLoading(false);
        }
    };

    // Reset selection
    const handleReset = () => {
        setSelectedYear(null);
        setSelectedLevel(null);
        setUserInfo({ firstname: "", lastname: "", email: "", phone: "" });
        setValidationErrors([]);
        setShowForm(false);
    };

    return (
        <>
            <Navbar />
            
            <div className="container mt-5 py-5">
                {/* Hero Section */}
                <div className="text-center mt-4 py-1 mb-5">
                    <h1 className="display-5 fw-bold text-primary mb-3">Complete Past Questions Package</h1>
                    <p className="lead mb-4">
                        Select your exam year and level to get all past questions for 
                        <span className="text-success fw-bold"> GHS 26.00</span> per year
                    </p>
                    
                    <div className="row justify-content-center">
                        <div className="col-md-8">
                            <div className="alert alert-info">
                                <div className="d-flex align-items-center">
                                    <i className="bi bi-info-circle fs-4 me-3"></i>
                                    <div className="text-start">
                                        <h6 className="mb-1">What you get:</h6>
                                        <p className="mb-0">All question papers, answer booklets, marking schemes, and examiner's reports for the selected year.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Step 1: Year Selection */}
                <div className="card mb-4 border-0 shadow-sm">
                    <div className="card-header bg-white border-bottom">
                        <h4 className="mb-0">
                            <span className="badge bg-primary me-2">1</span>
                            Select Exam Year
                        </h4>
                    </div>
                    <div className="card-body">
                        <p className="text-muted mb-4">Choose the year you want to purchase:</p>
                        
                        <div className="row">
                            {availableYears.map((year) => (
                                <div className="col-md-2 col-sm-4 col-6 mb-3" key={year}>
                                    <button
                                        className={`btn btn-lg w-100 ${selectedYear === year ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={() => handleYearSelect(year)}
                                        style={{
                                            height: '80px',
                                            fontSize: '1.2rem',
                                            fontWeight: '500'
                                        }}
                                    >
                                        {year}
                                        {selectedYear === year && (
                                            <i className="bi bi-check-circle ms-2"></i>
                                        )}
                                    </button>
                                </div>
                            ))}
                        </div>
                        
                        {selectedYear && (
                            <div className="alert alert-success mt-3">
                                <i className="bi bi-check-circle me-2"></i>
                                Selected: <strong>{selectedYear}</strong> Exam Year
                            </div>
                        )}
                    </div>
                </div>
                
                {/* Step 2: Level Selection (only shows if year is selected) */}
                {selectedYear && (
                    <div className="card mb-4 border-0 shadow-sm">
                        <div className="card-header bg-white border-bottom">
                            <h4 className="mb-0">
                                <span className="badge bg-success me-2">2</span>
                                Select Exam Level
                            </h4>
                        </div>
                        <div className="card-body">
                            <p className="text-muted mb-4">Choose your examination level:</p>
                            
                            <div className="row">
                                {examLevels.map((level) => (
                                    <div className="col-lg-4 mb-3" key={level.id}>
                                        <div 
                                            className={`card h-100 border-${level.color} ${selectedLevel?.id === level.id ? 'border-3 shadow' : ''}`}
                                            style={{ 
                                                transition: 'all 0.3s ease',
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => handleLevelSelect(level)}
                                        >
                                            <div className="card-body">
                                                <div className="d-flex align-items-center mb-3">
                                                    <i className={`bi ${level.icon} fs-2 text-${level.color} me-3`}></i>
                                                    <div>
                                                        <h5 className="card-title mb-0">{level.name}</h5>
                                                        <small className="text-muted">{level.fullName}</small>
                                                    </div>
                                                </div>
                                                
                                                <p className="card-text mb-3">{level.description}</p>
                                                
                                                <div className="mb-3">
                                                    <h6 className="text-dark mb-2">Subjects Included:</h6>
                                                    <p className="small mb-0">{level.subjects}</p>
                                                </div>
                                                
                                                <div className="mb-3">
                                                    <h6 className="text-dark mb-2">Features:</h6>
                                                    <ul className="list-unstyled small">
                                                        {level.features.map((feature, idx) => (
                                                            <li key={idx} className="mb-1">
                                                                <i className="bi bi-check-circle text-success me-2"></i>
                                                                {feature}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                            
                                            <div className="card-footer bg-transparent border-top-0 text-center">
                                                <button
                                                    className={`btn btn-${level.color} w-100 ${selectedLevel?.id === level.id ? 'active' : ''}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleLevelSelect(level);
                                                    }}
                                                >
                                                    {selectedLevel?.id === level.id ? (
                                                        <>
                                                            <i className="bi bi-check-circle me-2"></i>
                                                            Selected
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="bi bi-check me-2"></i>
                                                            Select {level.name}
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
               
                {showForm && selectedYear && selectedLevel && (
                    <div id="purchase-form" className="card mt-4 shadow">
                        <div className="card-header bg-white">
                            <div className="d-flex justify-content-between align-items-center">
                                <h4 className="mb-0">
                                    <span className="badge bg-info me-2">3</span>
                                    Complete Your Order
                                </h4>
                                <button 
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={handleReset}
                                >
                                    <i className="bi bi-arrow-left me-1"></i> Start Over
                                </button>
                            </div>
                        </div>
                        
                        <div className="card-body">
                            {/* Display validation errors at the top */}
                            {displayValidationErrors()}
                            
                            <div className="row">
                                <div className="col-lg-8">
                                    <div className="card border-primary mb-4">
                                        <div className="card-header bg-primary text-white">
                                            <h5 className="mb-0">Order Details</h5>
                                        </div>
                                        <div className="card-body">
                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <h6 className="text-muted">Exam Year:</h6>
                                                    <h4 className="text-primary">{selectedYear}</h4>
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <h6 className="text-muted">Exam Level:</h6>
                                                    <h4 className="text-primary">{selectedLevel.name} - {selectedLevel.fullName}</h4>
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <h6 className="text-muted">Subjects:</h6>
                                                    <p className="mb-0">{selectedLevel.subjects}</p>
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <h6 className="text-muted">Package Includes:</h6>
                                                    <ul className="list-unstyled mb-0">
                                                        {selectedLevel.features.map((feature, idx) => (
                                                            <li key={idx} className="small">
                                                                <i className="bi bi-check-circle text-success me-1"></i>
                                                                {feature}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="card border-secondary">
                                        <div className="card-header bg-light">
                                            <h5 className="mb-0">Customer Information</h5>
                                            <p className="text-muted mb-0 small">All fields are required</p>
                                        </div>
                                        <div className="card-body">
                                            <div className="row">
                                                {/* First Name */}
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">
                                                        First Name *
                                                        {validationErrors.some(e => e.toLowerCase().includes('first name')) && (
                                                            <span className="text-danger ms-2">
                                                                <i className="bi bi-exclamation-circle"></i>
                                                            </span>
                                                        )}
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className={`form-control ${validationErrors.some(e => e.toLowerCase().includes('first name')) ? 'is-invalid' : ''}`}
                                                        name="firstname"
                                                        value={userInfo.firstname}
                                                        onChange={handleInputChange}
                                                        placeholder="Enter your first name"
                                                        required
                                                    />
                                                    <div className="invalid-feedback">
                                                        Please enter your first name
                                                    </div>
                                                </div>
                                                
                                                {/* Last Name */}
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">
                                                        Last Name *
                                                        {validationErrors.some(e => e.toLowerCase().includes('last name')) && (
                                                            <span className="text-danger ms-2">
                                                                <i className="bi bi-exclamation-circle"></i>
                                                            </span>
                                                        )}
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className={`form-control ${validationErrors.some(e => e.toLowerCase().includes('last name')) ? 'is-invalid' : ''}`}
                                                        name="lastname"
                                                        value={userInfo.lastname}
                                                        onChange={handleInputChange}
                                                        placeholder="Enter your last name"
                                                        required
                                                    />
                                                    <div className="invalid-feedback">
                                                        Please enter your last name
                                                    </div>
                                                </div>
                                                
                                                {/* Email */}
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">
                                                        Email Address *
                                                        {validationErrors.some(e => e.toLowerCase().includes('email')) && (
                                                            <span className="text-danger ms-2">
                                                                <i className="bi bi-exclamation-circle"></i>
                                                            </span>
                                                        )}
                                                    </label>
                                                    <input
                                                        type="email"
                                                        className={`form-control ${validationErrors.some(e => e.toLowerCase().includes('email')) ? 'is-invalid' : ''}`}
                                                        name="email"
                                                        value={userInfo.email}
                                                        onChange={handleInputChange}
                                                        placeholder="your@email.com"
                                                        required
                                                    />
                                                    <div className="invalid-feedback">
                                                        Please enter a valid email address
                                                    </div>
                                                </div>
                                                
                                                {/* Phone Number */}
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">
                                                        Phone Number *
                                                        {validationErrors.some(e => e.toLowerCase().includes('phone')) && (
                                                            <span className="text-danger ms-2">
                                                                <i className="bi bi-exclamation-circle"></i>
                                                            </span>
                                                        )}
                                                    </label>
                                                    <input
                                                        type="tel"
                                                        className={`form-control ${validationErrors.some(e => e.toLowerCase().includes('phone')) ? 'is-invalid' : ''}`}
                                                        name="phone"
                                                        value={userInfo.phone}
                                                        onChange={handleInputChange}
                                                        placeholder="+233XXXXXXXXX or 0XXXXXXXXX"
                                                        required
                                                    />
                                                    <div className="invalid-feedback">
                                                        Please enter a valid Ghana phone number
                                                    </div>
                                                    <small className="text-muted">
                                                        Format: +233XXXXXXXXX or 0XXXXXXXXX
                                                    </small>
                                                </div>
                                                
                                                {/* Package Price Display */}
                                                <div className="col-md-12 mb-3">
                                                    <label className="form-label">Package Price</label>
                                                    <div className="form-control bg-light">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <span>Total Amount:</span>
                                                            <span className="fs-4 fw-bold text-success">GHS 26.00</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div className="alert alert-warning mt-3">
                                                <div className="d-flex">
                                                    <i className="bi bi-exclamation-triangle me-3 fs-4"></i>
                                                    <div>
                                                        <h6 className="mb-1">Important:</h6>
                                                        <p className="mb-0 small">
                                                            After payment, you will receive an email with download links for your selected {selectedYear} {selectedLevel.name} past questions. 
                                                            Access is valid for 1 year from purchase date.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                               
                                <div className="col-lg-4">
                                    <div className="card border-success sticky-top" style={{top: '20px'}}>
                                        <div className="card-header bg-success text-white">
                                            <h5 className="mb-0">Payment Summary</h5>
                                        </div>
                                        <div className="card-body">
                                            {/* Package Details */}
                                            <div className="mb-4">
                                                <h6 className="text-muted">You're purchasing:</h6>
                                                <h5 className="text-dark">{selectedYear} {selectedLevel.name}</h5>
                                                <p className="small text-muted mb-0">
                                                    Complete past questions package with all papers, answers, and marking schemes.
                                                </p>
                                            </div>
                                            
                                            <hr />
                                          
                                            <div className="mb-4">
                                                <div className="d-flex justify-content-between mb-2">
                                                    <span className="text-muted">Package Price:</span>
                                                    <span className="fw-semibold">GHS 26.00</span>
                                                </div>
                                                <div className="d-flex justify-content-between mb-2">
                                                    <span className="text-muted">Discount:</span>
                                                    <span className="text-success">-GHS 0.00</span>
                                                </div>
                                                <div className="d-flex justify-content-between mb-2">
                                                    <span className="text-muted">Processing Fee:</span>
                                                    <span className="fw-semibold">GHS 0.00</span>
                                                </div>
                                            </div>
                                            
                                            <hr />
                                            
                                            {/* Total */}
                                            <div className="d-flex justify-content-between mb-4">
                                                <h5 className="mb-0">Total:</h5>
                                                <h4 className="text-success mb-0">GHS 26.00</h4>
                                            </div>
                                            
                                            {/* Payment Button */}
                                            <div className="d-grid">
                                                <button
                                                    className="btn btn-success btn-lg"
                                                    onClick={handlePayment}
                                                    disabled={loading || validationErrors.length > 0}
                                                >
                                                    {loading ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                                            Processing Payment...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="bi bi-lock me-2"></i>
                                                            Proceed to Secure Payment
                                                        </>
                                                    )}
                                                </button>
                                                {validationErrors.length > 0 && (
                                                    <small className="text-danger text-center mt-2">
                                                        Please fix all errors before proceeding
                                                    </small>
                                                )}
                                            </div>
                                            
                                            <div className="mt-4 text-center">
                                                <div className="d-flex justify-content-center align-items-center mb-2">
                                                    <i className="bi bi-shield-check text-success me-2"></i>
                                                    <small className="text-muted">100% Secure Payment</small>
                                                </div>
                                                <small className="text-muted d-block">
                                                    <i className="bi bi-credit-card me-1"></i>
                                                    All major cards & mobile money accepted
                                                </small>
                                            </div>
                                            
                                            {/* Support Info */}
                                            <div className="mt-3 pt-3 border-top">
                                                <small className="text-muted">
                                                    <i className="bi bi-envelope me-1"></i>
                                                    Questions? <a href="mailto:support@pastq.eddcodesgh.com">support@pastq.eddcodesgh.com</a>
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
           
                {/* Call to Action */}
                <div className="text-center mt-5">
                    <h4 className="mb-3">Need help with your order?</h4>
                    <p className="text-muted mb-4">Contact our support team for assistance with your purchase.</p>
                    <a href="/contact" className="btn btn-outline-primary">
                        <i className="bi bi-envelope me-2"></i>
                        Contact Support
                    </a>
                </div>
            </div>
            
            <Footer />
            
            <style jsx>{`
                .card {
                    border-radius: 10px;
                }
                .card-header {
                    border-radius: 10px 10px 0 0 !important;
                }
                .sticky-top {
                    position: sticky;
                    border-radius: 10px;
                }
                .btn {
                    border-radius: 8px;
                }
                .form-control {
                    border-radius: 8px;
                    padding: 0.75rem 1rem;
                }
                .form-control.is-invalid {
                    border-color: #dc3545;
                    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' width='12' height='12' fill='none' stroke='%23dc3545'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23dc3545' stroke='none'/%3e%3c/svg%3e");
                    background-repeat: no-repeat;
                    background-position: right calc(0.375em + 0.1875rem) center;
                    background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
                }
                .accordion-button {
                    border-radius: 8px !important;
                    margin-bottom: 5px;
                }
                .accordion-button:not(.collapsed) {
                    background-color: #f8f9fa;
                    color: #0d6efd;
                }
                .badge {
                    font-weight: 500;
                    padding: 0.5em 0.8em;
                }
                .alert-danger ul {
                    margin-bottom: 0;
                    padding-left: 1.5rem;
                }
                .alert-danger li {
                    margin-bottom: 0.25rem;
                }
            `}</style>
        </>
    );
};

export default FullPackage;